import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import ConfirmationDialog from '../ConfirmationDialog';
import type { SavedCFSearch } from '../../types/campaignFinance';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

interface CFSavedFiltersProps {
  savedSearches: SavedCFSearch[];
  loading?: boolean;
  onApply: (search: SavedCFSearch) => void;
  onDelete: (id: string) => void;
  className?: string;
}

const CFSavedFilters: React.FC<CFSavedFiltersProps> = ({
  savedSearches,
  loading = false,
  onApply,
  onDelete,
  className = '',
}) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    onDelete(id);
    setDeleteId(null);
  };

  return (
    <div className={`card ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">My Filters</h3>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Quick access to your saved filter combinations</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      ) : savedSearches.length === 0 ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary italic">No saved filters yet</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {savedSearches.map(s => (
            <div
              key={s.id}
              className="surface-inset flex items-center justify-between p-3 hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary transition-colors"
            >
              <button
                onClick={() => onApply(s)}
                className="flex-1 text-left"
              >
                <div className="font-medium text-text-primary dark:text-text-dark-primary">{s.name}</div>
                <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                  Created {new Date(s.createdAt).toLocaleDateString()}
                </div>
              </button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setDeleteId(s.id);
                }}
                className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 dark:hover:text-error-500 transition-colors"
                title="Delete filter"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <ConfirmationDialog
          isOpen={true}
          title="Delete filter?"
          message="Are you sure you want to delete this saved filter? This action cannot be undone."
          confirmText="Delete"
          onConfirm={() => handleDelete(deleteId)}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
};

export default CFSavedFilters;
